import { Resources } from 'constants/resources';

import { useNumberBooleanChoices } from 'hooks/useNumberBooleanChoices';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimeRangePicker } from 'shared/mui/DatePicker/DateTimeRangePicker/DateTimeRangePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { AutocompleteMultiselectInput, SelectInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput } from 'shared/react-admin/ReferenceInputs';
import { User } from 'types/embedded/user';

export const useOrderAdjustmentsReportListFilterFields = (): FilterFieldConfig[] => {
  const { control } = useFormContext();
  const translate = useTypedTranslate();

  const numberBooleanChoices = useNumberBooleanChoices();

  return [
    {
      name: 'appliedDate',
      label: translate('app.orderAdjustmentsReport.filters.time'),
      chipType: 'dateTimeRange',
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangePicker
              label={translate('app.orderAdjustmentsReport.filters.createdAtPeriod')}
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'adjustmentType',
      label: translate('app.orderAdjustmentsReport.filters.adjustmentType'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.AdjustmentTypes,
        limitTags: 1,
        optionText: 'description',
      },
      input: (
        <ReferenceArrayInput
          perPage={100}
          reference={Resources.AdjustmentTypes}
          source="adjustmentType"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.orderAdjustmentsReport.filters.adjustmentType')}
            name="adjustmentType"
            optionText="description"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'impactToBalance',
      label: translate('app.orderAdjustmentsReport.filters.impactToBalance'),
      chipType: 'selectChoices',
      chipProps: {
        choices: numberBooleanChoices,
      },
      input: (
        <SelectInput
          choices={numberBooleanChoices}
          fullWidth
          helperText={false}
          id="impactToBalance"
          label={translate('app.orderAdjustmentsReport.filters.impactToBalance')}
          name="impactToBalance"
          optionText="label"
          source="impactToBalance"
          variant="outlined"
        />
      ),
    },
    {
      name: 'adjustmentStatus',
      label: translate('app.orderAdjustmentsReport.filters.adjustmentStatus'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.AdjustmentStatuses,
        limitTags: 1,
        optionText: 'description',
      },
      input: (
        <ReferenceArrayInput
          perPage={100}
          reference={Resources.AdjustmentStatuses}
          source="adjustmentStatus"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.orderAdjustmentsReport.filters.adjustmentStatus')}
            name="adjustmentStatus"
            optionText="description"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'user',
      label: translate('app.orderAdjustmentsReport.filters.user'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Users,
        limitTags: 1,
        optionText: 'firstName',
        renderChipLabel: (choice: User) => `${choice.firstName} ${choice.lastName}`,
      },
      input: (
        <ReferenceArrayInput perPage={200} reference={Resources.Users} source="user">
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ fullName: searchText })}
            getOptionLabel={(choice: User) => `${choice.firstName} ${choice.lastName}`}
            helperText={false}
            label={translate('app.orderAdjustmentsReport.filters.user')}
            name="user"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'requestId',
      label: translate('app.orderAdjustmentsReport.filters.requestId'),
      chipType: 'text',
      input: (
        <TextInput
          fullWidth
          helperText={false}
          label={translate('app.orderAdjustmentsReport.filters.requestId')}
          source="requestId"
          variant="outlined"
        />
      ),
    },
  ];
};
